import { cva, type VariantProps } from "class-variance-authority";
import {
  forwardRef,
  type HTMLAttributes,
  type TableHTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from "react";

import { cn } from "@/lib/utils";

/**
 * The table primitive (VISUAL_SYSTEM_PLAN U3.1).
 *
 * Nine `<table>` elements across eight files hand-rolled this, and no two
 * agreed: header tints split between `bg-muted/50` and `bg-surface-2`, row
 * separation between `border-t` on the row, `border-b` on the row and
 * `divide-y` on the body, header type between `text-xs tracking-wide` and
 * `text-xs tracking-wider`, and `font-medium` on the header cell in four of
 * the nine. None of those differences meant anything — they are what happens
 * when the same recipe is retyped nine times.
 *
 * **`Table` is the `<table>`, not a wrapper around one.** shadcn's version
 * wraps it in `<div className="relative w-full overflow-auto">`; every call
 * site here already brings its own wrapper (`overflow-hidden rounded-md
 * border`, `overflow-x-auto`, a card), and silently adding a second scroll
 * container underneath nine existing ones is how the scroll clamp that the
 * split pane, the tables and the 3D canvas depend on gets broken. The
 * enclosing box stays the caller's.
 *
 * Variants exist only where two call sites genuinely disagreed:
 * `TableHeader`'s `surface`, `TableRow`'s `interactive`, and the `align` on
 * head and body cells. There is no `center` alignment and no size scale
 * because nothing consumes them — the unit's own rule against adding
 * primitives nothing consumes applies to variants too.
 *
 * Spacing is `px-3 py-row-y` throughout, so row height stays on `--density-row-y`
 * and follows `[data-density]` (U2.1). Do not write a literal `py-*` here.
 */

export const Table = forwardRef<HTMLTableElement, TableHTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <table ref={ref} className={cn("w-full text-sm", className)} {...props} />
  )
);
Table.displayName = "Table";

const tableHeaderVariants = cva(
  "border-b border-border-subtle label-micro text-left text-muted-foreground",
  {
    variants: {
      surface: {
        /** The header sits on whatever is behind the table. */
        plain: "",
        /** A tinted header band, for a table that owns its own bordered box. */
        tinted: "bg-surface-2",
      },
    },
    defaultVariants: { surface: "plain" },
  }
);

export interface TableHeaderProps
  extends HTMLAttributes<HTMLTableSectionElement>,
    VariantProps<typeof tableHeaderVariants> {}

export const TableHeader = forwardRef<HTMLTableSectionElement, TableHeaderProps>(
  ({ className, surface, ...props }, ref) => (
    <thead ref={ref} className={cn(tableHeaderVariants({ surface }), className)} {...props} />
  )
);
TableHeader.displayName = "TableHeader";

export const TableBody = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => <tbody ref={ref} className={cn(className)} {...props} />);
TableBody.displayName = "TableBody";

const tableRowVariants = cva("border-b border-border-subtle last:border-0", {
  variants: {
    interactive: {
      /** A row that is only read. */
      none: "",
      /** A row that responds to the pointer — hover feedback, no cursor
       *  change. A row that also NAVIGATES adds `cursor-pointer` itself, so
       *  "this reacts" and "this is a link" stay distinguishable. */
      hover: "transition-colors hover:bg-surface-2",
    },
  },
  defaultVariants: { interactive: "none" },
});

export interface TableRowProps
  extends HTMLAttributes<HTMLTableRowElement>,
    VariantProps<typeof tableRowVariants> {}

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, interactive, ...props }, ref) => (
    <tr ref={ref} className={cn(tableRowVariants({ interactive }), className)} {...props} />
  )
);
TableRow.displayName = "TableRow";

const tableCellVariants = cva("px-3 py-row-y", {
  variants: {
    align: { left: "text-left", right: "text-right" },
  },
  defaultVariants: { align: "left" },
});

/**
 * `align` is the cva variant, so the deprecated presentational `align`
 * attribute (`"center" | "justify" | "char" | …`, which HTML5 dropped) is
 * omitted rather than left to widen the variant's type. Keeping both would
 * type-check only by admitting values the variant has no class for, which is
 * how a silently-unstyled `align="char"` reaches a cell.
 */
export interface TableHeadProps
  extends Omit<ThHTMLAttributes<HTMLTableCellElement>, "align">,
    VariantProps<typeof tableCellVariants> {}

export const TableHead = forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, align, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(tableCellVariants({ align }), "font-medium", className)}
      {...props}
    />
  )
);
TableHead.displayName = "TableHead";

/** `align` is the cva variant here too — see `TableHeadProps`. */
export interface TableCellProps
  extends Omit<TdHTMLAttributes<HTMLTableCellElement>, "align">,
    VariantProps<typeof tableCellVariants> {}

export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, align, ...props }, ref) => (
    <td ref={ref} className={cn(tableCellVariants({ align }), className)} {...props} />
  )
);
TableCell.displayName = "TableCell";

/**
 * A screen-reader caption.
 *
 * Both existing captions are `sr-only` — the table already carries a visible
 * heading above it, and the caption is there so the accessibility tree names
 * the table. That is the default here rather than shadcn's visible caption,
 * because a visible one has no consumer. A caller that wants one passes
 * `className="not-sr-only …"`.
 */
export const TableCaption = forwardRef<
  HTMLTableCaptionElement,
  HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption ref={ref} className={cn("sr-only", className)} {...props} />
));
TableCaption.displayName = "TableCaption";
